import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import type { VerantRole } from "../components/verantwortlichkeit/VerantwortlichkeitInput";

export type AltkundenStatus = "open" | "bereinigt";

/** One row in the Altkunden work list (status Altkunde, no open duplicate). */
export interface AltkundeRow {
  id: number;
  lastname: string | null;
  firstName: string | null;
  birthDate: string | null;
  phone: string | null;
  email: string | null;
  contractCount: number;
  statusLabel: string | null;
  verantwortlich: string | null;
  bereinigtAt: string | null;
}

/** A linked contact (company or person) in the detail overview. */
export interface AltkundeRelationRef {
  relationId: number;
  contactId: number;
  lastname: string | null;
  firstName: string | null;
  relationType: string | null;
  function: string | null;
  isExtern: boolean;
  share: string | null;
  sameHousehold: boolean;
}

export interface AltkundeAddress {
  label: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  ortsteil: string | null;
  country: string | null;
  isDefault: boolean;
}
export interface AltkundeComm {
  type: string;
  value: string | null;
  label: string | null;
  isDefault: boolean;
}
export interface AltkundeBank {
  bankName: string | null;
  bankDescription: string | null;
  iban: string | null;
  bic: string | null;
  accountHolder: string | null;
  directDebit: boolean;
  isDefault: boolean;
}

export interface AltkundeDetail {
  contact: {
    id: number;
    contactType: string | null;
    lastname: string | null;
    firstName: string | null;
    callName: string | null;
    gender: string | null;
    birthDate: string | null;
    birthPlace: string | null;
    nationality: string | null;
    maritalStatus: string | null;
    occupation: string | null;
    jobStatus: string | null;
    employer: string | null;
    industry: string | null;
    remark: string | null;
    taxNumber: string | null;
    gruendungsjahr: number | null;
    gruendungsdatum: string | null;
    register: string | null;
    handelsregisterNummer: string | null;
    umsatzsteuernummer: string | null;
    steuernummer: string | null;
    finanzamt: string | null;
    salutationLabel: string | null;
    titleLabel: string | null;
    legalFormLabel: string | null;
    statusId: number | null;
    statusLabel: string | null;
    deleted: string | null;
    deleteReason: string | null;
    bereinigtAt: string | null;
    contractCount: number;
    phone: string | null;
    email: string | null;
  };
  addresses: AltkundeAddress[];
  communications: AltkundeComm[];
  bankAccounts: AltkundeBank[];
  extraStatuses: string[];
  companies: AltkundeRelationRef[];
  persons: AltkundeRelationRef[];
  kundenverbindung: {
    id: number;
    name: string | null;
    members: Array<{ id: number; name: string; contactType: string | null }>;
  } | null;
}

export interface ContactStatusOption {
  id: number;
  status: string;
}

/** Minimal new company to create on the fly (Branche/Rechtsform/Tel/Mail Pflicht bei Anlage). */
export interface NewCompanyInput {
  name: string;
  phone?: string;
  email?: string;
  web?: string;
  branche?: string;
  legalFormId?: number;
}

export interface ClassifyInput {
  targetCompanyId?: number;
  targetCompanyNew?: NewCompanyInput;
  isExtern?: boolean;
  roles?: VerantRole[];
  externalCompanyId?: number;
  externalCompanyNew?: NewCompanyInput;
  isMitarbeiter?: boolean;
  isMenschImUnternehmen?: boolean;
}

/** Work list (Altkunde rows) + total for the header. */
export const listAltkunden = async (
  status: AltkundenStatus,
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: AltkundeRow[]; total: number }> => {
  const { data, total } = await apiFetch<AltkundeRow[]>("altkunden-cleanup", {
    params: {
      status,
      ...(teamId ? { teamId } : {}),
      ...(q ? { q } : {}),
      limit,
      offset,
    },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

export const listAltkundenTeams = async (): Promise<
  Array<{ id: string; name: string | null }>
> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "altkunden-cleanup/teams",
  );
  return data ?? [];
};

export const fetchAltkundeStatuses = async (): Promise<ContactStatusOption[]> => {
  const { data } = await apiFetch<ContactStatusOption[]>("altkunden-cleanup/statuses");
  return data ?? [];
};

export const fetchAltkundeDetail = async (id: number): Promise<AltkundeDetail> => {
  const { data } = await apiFetch<AltkundeDetail>(`altkunden-cleanup/${id}`);
  return data;
};

const postJson = async (path: string, body: unknown, failMsg: string): Promise<any> => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${failMsg}: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json().catch(() => ({}));
};

/**
 * Reclassify a contact's status (e.g. Altkunde → Kunde).
 *
 * SP6: Bei „Interessent + NKG" (`nkg === true`) legt das Backend zusätzlich
 * einen NKG-Vorgang an (Owner = handelnder User, ohne Frist/Todo, idempotent)
 * und liefert dessen Id zurück.
 */
export const reclassifyAltkunde = (
  id: number,
  contactStatusId: number,
  nkg = false,
): Promise<{ nkgVorgangId: number | null }> =>
  postJson(
    `altkunden-cleanup/${id}/reclassify`,
    { contactStatusId, nkg },
    "Umklassifizieren fehlgeschlagen",
  );

/** Classify as „Mensch fürs/im Unternehmen" — creates the relation(s) to a target company. */
export const classifyAltkunde = (id: number, input: ClassifyInput): Promise<void> =>
  postJson(`altkunden-cleanup/${id}/classify`, input, "Klassifizieren fehlgeschlagen");

/**
 * Soft-delete the contact (Papierkorb).
 *
 * SP5: Der Grund ist optional — im Altkunden-Flow „Gibt es den Kontakt noch?"
 * → NEIN wird er weggelassen; das Backend setzt dann einen festen Default-Grund
 * („Altkunden-Bereinigung: automatisch gelöscht").
 */
export const deleteAltkunde = (id: number, reason?: string): Promise<void> =>
  postJson(
    `altkunden-cleanup/${id}/delete`,
    reason && reason.trim() ? { reason } : {},
    "Löschen fehlgeschlagen",
  );
